import { ReviewOfferProgram } from "./types";

/**
 * All review-for-discount offer copy lives HERE and only here (HUB-453,
 * extended HUB-466 for the two-program discount — 5% at checkout /
 * 10% post-campaign, epic HUB-440 phase 2). This file is the compliance-lint
 * surface for FTC 16 CFR Part 465: the offer is for leaving *a* review,
 * any rating — never for a *positive* one. scripts/ftc-compliance-check.mjs
 * scans this file; see that script for the exact checks.
 *
 * Banned in any string added to this file: sentiment words ("great",
 * "positive", "5-star", "amazing", "love"), star-count steering ("give us
 * 5 stars"), or anything implying rating outcome affects eligibility.
 * "Get {percent}% off by sharing a review" — OK. "Leave us a great review" — NOT OK.
 *
 * Percent is NEVER a literal digit here (HUB-466) — every percent-bearing
 * string uses the REVIEW_OFFER_PERCENT_PLACEHOLDER token ("{percent}"),
 * filled in at render time from the entitlement/eligibility response's
 * `discount_percent` via formatReviewOfferCopy() below — never a hardcoded
 * "5%". The percent is read from the entitlement/eligibility ROW, never a
 * program registry default: what a client was granted or shown must never
 * silently re-price (see oppizi-api's ReviewDiscountPrograms.ts for the
 * server-side version of this same rule).
 *
 * Per-program map (HUB-466): `reviewOfferCopy` is keyed by
 * `ReviewOfferProgram` so scripts/ftc-compliance-check.mjs keeps scanning
 * every string that could ever render, even if a future program's wording
 * diverges from the others (e.g. a "your next campaign" callout unique to
 * POST_CAMPAIGN_10). Today CHECKOUT_5 and POST_CAMPAIGN_10 read identically
 * apart from the interpolated percent — that's intentional, not a
 * placeholder for missing work.
 *
 * Source of truth: /Users/slava/Desktop/handoff/frontend-spec.md ("Copy tokens").
 */

export const REVIEW_OFFER_PERCENT_PLACEHOLDER = "{percent}";

export type ReviewOfferCopySet = {
  offer: {
    /** Contains REVIEW_OFFER_PERCENT_PLACEHOLDER — run through formatReviewOfferCopy() before rendering. */
    headline: string;
    cta: string;
    applying: string;
    applied: {
      /** Contains REVIEW_OFFER_PERCENT_PLACEHOLDER — run through formatReviewOfferCopy() before rendering. */
      title: string;
      sub: string;
    };
    exclusiveTooltip: string;
    error: {
      title: string;
      retry: string;
    };
    swap: {
      /** Contains REVIEW_OFFER_PERCENT_PLACEHOLDER — run through formatReviewOfferCopy() before rendering. */
      title: string;
      /** Contains REVIEW_OFFER_PERCENT_PLACEHOLDER — run through formatReviewOfferCopy() before rendering. */
      reverse: string;
      body: string;
      keep: string;
      switch: string;
      error: string;
      retry: string;
    };
  };
  summary: {
    /** Contains REVIEW_OFFER_PERCENT_PLACEHOLDER — run through formatReviewOfferCopy() before rendering. */
    discountLine: string;
  };
  review: {
    // HUB-458 (FTC 16 CFR Part 465, contract §1 "MUST ... Display the
    // incentivized-review disclosure ... before submit"). Wording matches
    // the frozen reference fixture
    // (Obsidian oads-discount/contracts/thanos-widget-fixture.html:12).
    // Program-agnostic today — no percent is mentioned in the disclosure
    // itself, so it does not need REVIEW_OFFER_PERCENT_PLACEHOLDER.
    incentiveDisclosure: string;
    g2Notice: string;
    consent: string;
  };
};

export const reviewOfferCopy: Record<ReviewOfferProgram, ReviewOfferCopySet> = {
  CHECKOUT_5: {
    offer: {
      headline: "Want a {percent}% discount? Leave us a review.",
      cta: "Leave a review",
      applying: "Applying your discount",
      applied: {
        title: "{percent}% review discount applied",
        sub: "Thanks for your review!",
      },
      exclusiveTooltip: "One discount per order",
      error: {
        title: "We couldn't apply your discount",
        retry: "Retry",
      },
      swap: {
        title: "Replace your {percent}% review discount with a promo code?",
        reverse: "Replace your promo code with the {percent}% review discount?",
        body: "Only one discount can be applied per order. Your published review is never affected.",
        keep: "Keep current",
        switch: "Switch",
        error: "We couldn't update your discount. Your current discount is unchanged — please try again.",
        retry: "Retry",
      },
    },
    summary: {
      discountLine: "Review discount ({percent}%)",
    },
    review: {
      incentiveDisclosure:
        "Disclosure: you are receiving a discount for submitting this review. You write your review on G2, and G2 publishes it under its own review guidelines.",
      g2Notice: "Your email will be shared with G2 to verify your review.",
      consent:
        "By submitting, you agree that your review, name, title, and company may be published on Oppizi review pages and G2.",
    },
  },
  POST_CAMPAIGN_10: {
    offer: {
      headline: "Want a {percent}% discount? Leave us a review.",
      cta: "Leave a review",
      applying: "Applying your discount",
      applied: {
        title: "{percent}% review discount applied",
        sub: "Thanks for your review!",
      },
      exclusiveTooltip: "One discount per order",
      error: {
        title: "We couldn't apply your discount",
        retry: "Retry",
      },
      swap: {
        title: "Replace your {percent}% review discount with a promo code?",
        reverse: "Replace your promo code with the {percent}% review discount?",
        body: "Only one discount can be applied per order. Your published review is never affected.",
        keep: "Keep current",
        switch: "Switch",
        error: "We couldn't update your discount. Your current discount is unchanged — please try again.",
        retry: "Retry",
      },
    },
    summary: {
      discountLine: "Review discount ({percent}%)",
    },
    review: {
      incentiveDisclosure:
        "Disclosure: you are receiving a discount for submitting this review. You write your review on G2, and G2 publishes it under its own review guidelines.",
      g2Notice: "Your email will be shared with G2 to verify your review.",
      consent:
        "By submitting, you agree that your review, name, title, and company may be published on Oppizi review pages and G2.",
    },
  },
} as const;

/** Program-agnostic accessor — the single call site every consumer should use. */
export const getReviewOfferCopy = (program: ReviewOfferProgram): ReviewOfferCopySet => reviewOfferCopy[program];

/**
 * Single interpolation point — replaces every REVIEW_OFFER_PERCENT_PLACEHOLDER
 * token with the real percent, read from the entitlement/eligibility row.
 * Never format a percent literal directly into a copy string anywhere else —
 * that's the bug this ticket (HUB-466) exists to fix.
 */
export const formatReviewOfferCopy = (template: string, percent: number): string =>
  template.split(REVIEW_OFFER_PERCENT_PLACEHOLDER).join(String(percent));

// Banned-word list used by C1's compliance lint suite (HUB-456) and by
// the dev-time assertion below — kept here so the lint rule and the copy
// it's checking stay in the same file.
export const REVIEW_OFFER_BANNED_WORDS = [
  "great",
  "amazing",
  "positive",
  "5-star",
  "five-star",
  "love",
  "excellent",
  "best",
] as const;

if (process.env.NODE_ENV !== "production") {
  const flat = JSON.stringify(reviewOfferCopy).toLowerCase();
  const offender = REVIEW_OFFER_BANNED_WORDS.find((word) => flat.includes(word));
  if (offender) {
    // eslint-disable-next-line no-console
    console.error(`[reviewOfferCopy] Banned sentiment word "${offender}" found in copy tokens (FTC 16 CFR 465).`);
  }
}
